import { useState, useEffect } from "react";
import RecordPaymentModal from "@/components/admin/RecordPaymentModal";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { CalendarDays, CalendarClock, Loader2, Search, Clock, Video, MapPin, Check, LogIn, CheckCircle2, UserX, Wallet, Users, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import RescheduleModal from "@/components/manager/RescheduleModal";
import { toast } from "sonner";
import { fmtDate, fmtTimeRange } from "@/lib/format";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_CLR = {
  pending: "bg-[#F1EEE8] text-[#8FA89B]", awaiting_approval: "bg-[#D98E73]/20 text-[#B5794E]",
  confirmed: "bg-[#144F75]/12 text-[#144F75]", approved: "bg-[#144F75]/12 text-[#144F75]",
  checked_in: "bg-[#5C8A72]/15 text-[#5C8A72]", in_session: "bg-[#5C8A72]/15 text-[#5C8A72]",
  completed: "bg-[#5C8A72]/15 text-[#5C8A72]", cancelled: "bg-[#B45151]/12 text-[#B45151]",
  no_show: "bg-[#B45151]/12 text-[#B45151]",
};
const PAY_CLR = {
  paid: "bg-[#5C8A72]/15 text-[#5C8A72]", deposit: "bg-[#B5794E]/15 text-[#B5794E]",
  partial: "bg-[#B5794E]/15 text-[#B5794E]", unpaid: "bg-[#F1EEE8] text-[#8FA89B]",
  free: "bg-[#F1EEE8] text-[#8FA89B]", refunded: "bg-[#B45151]/12 text-[#B45151]", partial_refund: "bg-[#B45151]/12 text-[#B45151]",
};

const CHIPS = [
  { key: "", label: "All" },
  { key: "today", label: "Today", count: "today" },
  { key: "upcoming", label: "Upcoming", count: "upcoming" },
  { key: "pending", label: "Pending approval", count: "pending" },
  { key: "outstanding", label: "Outstanding balance", count: "outstanding" },
  { key: "no_show", label: "No-shows", count: "no_show" },
  { key: "refunded", label: "Refunded" },
];

export default function AppointmentsPanel({ initialPreset = "", base = "/admin", focusId = "" }) {
  const nav = useNavigate();
  const [preset, setPreset] = useState(initialPreset);
  const [q, setQ] = useState("");
  const [payFor, setPayFor] = useState(null);
  const [dfrom, setDfrom] = useState("");
  const [dto, setDto] = useState("");
  const [range, setRange] = useState("");
  const [hl, setHl] = useState("");

  const applyRange = (key) => {
    const d = new Date();
    const iso = (x) => x.toISOString().slice(0, 10);
    let from = new Date(d), to = new Date(d);
    if (key === "week") {
      from = new Date(d); from.setDate(d.getDate() - d.getDay());       // Sunday
      to = new Date(from); to.setDate(from.getDate() + 6);              // Saturday
    } else if (key === "month") {
      from = new Date(d.getFullYear(), d.getMonth(), 1);
      to = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    }
    setPreset(""); setRange(key); setDfrom(iso(from)); setDto(iso(to));
  };

  useEffect(() => { setPreset(initialPreset); }, [initialPreset]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin-appointments", base, preset, q, dfrom, dto],
    queryFn: () => api.get(`${base}/appointments`, { params: { preset, q, dfrom, dto } }).then((r) => r.data),
  });
  const rows = data?.appointments || [];
  const counts = data?.counts || {};

  useEffect(() => {
    if (!focusId || isLoading) return;
    setPreset(""); setQ(""); setDfrom(""); setDto(""); setRange("");
    setHl(focusId);
    const el = document.querySelector(`[data-testid="appt-row-${focusId}"]`) || document.querySelector(`[data-testid="appt-card-${focusId}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    const t = setTimeout(() => setHl(""), 4500);
    return () => clearTimeout(t);
  }, [focusId, isLoading, rows.length]);

  const [acting, setActing] = useState("");
  const setStatus = async (id, status) => {
    setActing(id + status);
    try {
      await api.put(`${base}/appointments/${id}/status`, { status });
      toast.success(`Marked ${status.replace("_", " ")}`);
      refetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); } finally { setActing(""); }
  };
  const actionsFor = (r) => {
    const btns = [];
    if (["pending", "awaiting_approval"].includes(r.status)) btns.push({ s: "confirmed", label: "Approve", Icon: Check });
    if (["confirmed", "approved"].includes(r.status)) btns.push({ s: "checked_in", label: "Check in", Icon: LogIn });
    if (["checked_in", "in_session", "confirmed"].includes(r.status)) btns.push({ s: "completed", label: "Complete", Icon: CheckCircle2 });
    if (!["cancelled", "completed", "no_show"].includes(r.status)) btns.push({ s: "no_show", label: "No-show", Icon: UserX });
    return btns;
  };
  const paidState = (r) => Number(r.amount_paid || 0) > 0 || ["paid", "deposit", "partial", "balance_due"].includes(r.payment_status);
  const canDelete = (r) => base === "/manager" && r.status === "pending" && !paidState(r);
  const delAppt = async (id) => {
    if (!window.confirm("Permanently delete this appointment? Only unconfirmed, unpaid records can be deleted — this cannot be undone. Use No-show/Cancel to keep history.")) return;
    setActing(id + "del");
    try {
      await api.delete(`${base}/appointments/${id}`);
      toast.success("Appointment deleted");
      refetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); } finally { setActing(""); }
  };

  return (
    <div data-testid="appointments-panel">
      {payFor && <RecordPaymentModal kind="booking" refId={payFor} onClose={() => { setPayFor(null); refetch(); }} />}
      <div className="mb-4">
        <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><CalendarDays size={22} className="text-[#144F75]" /> Appointments</h2>
        <p className="text-sm text-[#4A5D6B] mt-1">Every appointment across all centers. Filter by status, payment, date, or search a client.</p>
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="appt-chips">
        {CHIPS.map((c) => (
          <button key={c.key || "all"} onClick={() => { setRange(""); setDfrom(""); setDto(""); setPreset(c.key); }} data-testid={`appt-chip-${c.key || "all"}`}
            className={`px-3.5 py-1.5 rounded-full text-sm ${preset === c.key ? "bg-[#144F75] text-white" : "bg-[#F1EEE8] text-[#4A5D6B] hover:bg-[#E5E0D8]"}`}>
            {c.label}{c.count != null && counts[c.count] != null ? ` (${counts[c.count]})` : ""}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8FA89B]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client, email, service, enhancer, phone…" data-testid="appt-search" className="eyw-input w-full pl-9 text-sm" />
        </div>
        <div className="inline-flex rounded-full bg-[#F1EEE8] p-0.5" data-testid="appt-range-toggle">
          {[
            { key: "day", label: "Today" },
            { key: "week", label: "This week" },
            { key: "month", label: "This month" },
          ].map((r) => (
            <button key={r.key} onClick={() => applyRange(r.key)} data-testid={`appt-range-${r.key}`}
              className={`px-3 py-1.5 rounded-full text-sm transition-colors ${range === r.key ? "bg-[#144F75] text-white" : "text-[#4A5D6B] hover:bg-[#E5E0D8]"}`}>
              {r.label}
            </button>
          ))}
        </div>
        <input type="date" value={dfrom} onChange={(e) => { setRange(""); setDfrom(e.target.value); }} data-testid="appt-from" className="eyw-input !py-2 text-sm" />
        <span className="text-[#8FA89B] text-sm">to</span>
        <input type="date" value={dto} onChange={(e) => { setRange(""); setDto(e.target.value); }} data-testid="appt-to" className="eyw-input !py-2 text-sm" />
      </div>

      {isLoading ? (
        <div className="p-10 text-center text-[#8FA89B]"><Loader2 className="animate-spin inline" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="eyw-card p-10 text-center text-[#8FA89B]" data-testid="appt-empty">No appointments match these filters.</div>
      ) : (
        <>
        {/* Desktop table */}
        <div className="overflow-x-auto eyw-card p-0 hidden md:block" data-testid="appt-table">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-[#8FA89B] border-b border-[#E5E0D8]">
              <th className="py-3 px-4">When</th><th className="py-3 px-2">Service</th><th className="py-3 px-2">Client</th>
              <th className="py-3 px-2">Enhancer</th><th className="py-3 px-2">Status</th><th className="py-3 px-2">Payment</th><th className="py-3 px-4 text-right">Balance</th><th className="py-3 px-4 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className={`border-b border-[#F1EEE8] hover:bg-[#F9F7F3] transition-colors ${hl === r.id ? "ring-2 ring-inset ring-[#144F75] bg-[#144F75]/[0.05]" : ""}`} data-testid={`appt-row-${r.id}`}>
                  <td className="py-2.5 px-4 whitespace-nowrap"><div className="text-[#1A242B]">{fmtDate(r.date)}</div><div className="text-xs text-[#8FA89B] flex items-center gap-1"><Clock size={11} /> {fmtTimeRange(r.start_time)}{r.delivery_method === "zoom" || r.delivery_method === "google_meet" ? <Video size={11} className="ml-1" /> : r.room ? <MapPin size={11} className="ml-1" /> : null}</div></td>
                  <td className="py-2.5 px-2 text-[#1A242B]">{r.service_title || "—"}</td>
                  <td className="py-2.5 px-2"><div className="text-[#1A242B]">{r.client || "—"}</div>{r.phone && <div className="text-xs text-[#8FA89B]">{r.phone}</div>}</td>
                  <td className="py-2.5 px-2 text-[#4A5D6B]">{r.enhancer_name || "—"}</td>
                  <td className="py-2.5 px-2"><span className={`text-xs px-2 py-0.5 rounded-full capitalize ${STATUS_CLR[r.status] || STATUS_CLR.pending}`}>{r.status.replace("_", " ")}</span></td>
                  <td className="py-2.5 px-2"><span className={`text-xs px-2 py-0.5 rounded-full capitalize ${PAY_CLR[r.payment_status] || PAY_CLR.unpaid}`}>{r.payment_status.replace("_", " ")}</span></td>
                  <td className="py-2.5 px-4 text-right font-medium" style={{ color: r.balance_due > 0 ? "#B5794E" : "#1A242B" }}>{r.balance_due > 0 ? money(r.balance_due) : "—"}</td>
                  <td className="py-2.5 px-4 text-right whitespace-nowrap">
                    <div className="inline-flex gap-1.5 justify-end flex-wrap items-center">
                      {r.balance_due > 0 && r.status !== "cancelled" && (
                        <button onClick={() => nav("/pos?tab=balances")} data-testid={`appt-collect-${r.id}`} title="Collect balance at front desk"
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-[#B5794E]/15 text-[#B5794E] hover:bg-[#B5794E] hover:text-white transition-colors"><Wallet size={11} /> Collect</button>
                      )}
                      {base === "/admin" && (r.balance_due > 0 || !["paid", "free", "package", "refunded"].includes(r.payment_status)) && r.status !== "cancelled" && (
                        <button onClick={() => setPayFor(r.id)} data-testid={`appt-record-payment-${r.id}`} title="Record a gift card, promo, comp or other payment"
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-[#144F75]/10 text-[#144F75] hover:bg-[#144F75] hover:text-white transition-colors"><Wallet size={11} /> Record payment</button>
                      )}
                      <ActionButtons r={r} actionsFor={actionsFor} setStatus={setStatus} acting={acting} canDelete={canDelete(r)} onDelete={delAppt} />
                      <RescheduleControl r={r} refetch={refetch} base={base} />
                      <ReassignControl r={r} refetch={refetch} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden space-y-3" data-testid="appt-cards">
          {rows.map((r) => (
            <div key={r.id} className={`eyw-card p-4 transition-colors ${hl === r.id ? "ring-2 ring-[#144F75] bg-[#144F75]/[0.05]" : ""}`} data-testid={`appt-card-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-[#1A242B] truncate">{r.service_title || "—"}</div>
                  <div className="text-xs text-[#8FA89B] flex items-center gap-1 mt-0.5"><Clock size={11} /> {fmtDate(r.date)} · {fmtTimeRange(r.start_time)}{r.delivery_method === "zoom" || r.delivery_method === "google_meet" ? <Video size={11} className="ml-1" /> : r.room ? <MapPin size={11} className="ml-1" /> : null}</div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full capitalize shrink-0 ${STATUS_CLR[r.status] || STATUS_CLR.pending}`}>{r.status.replace("_", " ")}</span>
              </div>
              <div className="text-sm text-[#4A5D6B] mt-2">{r.client || "—"}{r.phone ? ` · ${r.phone}` : ""}{r.enhancer_name ? ` · ${r.enhancer_name}` : ""}</div>
              <div className="flex items-center justify-between mt-2">
                <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${PAY_CLR[r.payment_status] || PAY_CLR.unpaid}`}>{r.payment_status.replace("_", " ")}</span>
                {r.balance_due > 0 && <span className="text-sm font-medium text-[#B5794E]">{money(r.balance_due)} due</span>}
              </div>
              <div className="flex gap-1.5 flex-wrap mt-3">
                {r.balance_due > 0 && r.status !== "cancelled" && (
                  <button onClick={() => nav("/pos?tab=balances")} data-testid={`appt-collect-${r.id}`}
                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-[#B5794E]/15 text-[#B5794E] hover:bg-[#B5794E] hover:text-white transition-colors"><Wallet size={11} /> Collect</button>
                )}
                <ActionButtons r={r} actionsFor={actionsFor} setStatus={setStatus} acting={acting} canDelete={canDelete(r)} onDelete={delAppt} />
                <RescheduleControl r={r} refetch={refetch} base={base} />
                <ReassignControl r={r} refetch={refetch} />
              </div>
            </div>
          ))}
        </div>
        </>
      )}
    </div>
  );
}

function RescheduleControl({ r, refetch, base }) {
  const [open, setOpen] = useState(false);
  const [deciding, setDeciding] = useState("");
  if (base !== "/manager" || r.status === "cancelled") return null;
  const rr = r.reschedule_request;
  const decide = async (action) => {
    setDeciding(action);
    try {
      await api.put(`/manager/appointments/${r.id}/reschedule-request`, { action, note: "" });
      toast.success(action === "approve" ? `Approved — moved to ${rr.date} at ${fmtTimeRange(rr.start_time)}. Client notified.` : "Declined — the client keeps their original time and has been notified.");
      refetch();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDeciding(""); }
  };
  return (
    <>
      {rr?.status === "pending" && (
        <span className="inline-flex items-center gap-1.5 text-xs bg-[#E0A458]/15 border border-[#E0A458]/40 text-[#8A5A2B] rounded-lg px-2 py-1" data-testid={`resched-request-${r.id}`}>
          Client requests {fmtDate(rr.date)} · {fmtTimeRange(rr.start_time)}
          <button onClick={() => decide("approve")} disabled={!!deciding} data-testid={`resched-approve-${r.id}`} className="rounded-md bg-[#5C8A72] text-white px-2 py-0.5 hover:bg-[#4a7560] disabled:opacity-50">{deciding === "approve" ? "…" : "Approve"}</button>
          <button onClick={() => decide("decline")} disabled={!!deciding} data-testid={`resched-decline-${r.id}`} className="rounded-md bg-white border border-[#E5E0D8] text-[#4A5D6B] px-2 py-0.5 hover:border-red-400 hover:text-red-600 disabled:opacity-50">{deciding === "decline" ? "…" : "Decline"}</button>
        </span>
      )}
      <button onClick={() => setOpen(true)} data-testid={`appt-reschedule-${r.id}`} title="Reschedule"
        className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-[#F1EEE8] text-[#4A5D6B] hover:bg-[#144F75] hover:text-white transition-colors"><CalendarClock size={11} /> Reschedule</button>
      {open && <RescheduleModal booking={r} onClose={() => setOpen(false)} onDone={refetch} />}
    </>
  );
}

function ReassignControl({ r, refetch }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const { data } = useQuery({
    queryKey: ["reassign-providers", r.service_id, r.location_id],
    queryFn: () => api.get(`/services/${r.service_id}/providers`, { params: { location_id: r.location_id } }).then((x) => x.data),
    enabled: open && !!r.service_id,
  });
  const providers = data?.providers || [];
  if (!r.service_id || ["cancelled", "completed", "no_show"].includes(r.status)) return null;
  const assign = async (eid) => {
    setBusy(true);
    try {
      await api.patch(`/appointments/${r.id}/assign`, { enhancer_id: eid });
      toast.success("Enhancer reassigned — provider & center notified.");
      setOpen(false); refetch();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(formatApiError(d));
    } finally { setBusy(false); }
  };
  return (
    <div className="relative inline-block">
      <button onClick={() => setOpen((o) => !o)} data-testid={`appt-reassign-${r.id}`} title="Reassign Enhancer"
        className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-[#F1EEE8] text-[#4A5D6B] hover:bg-[#144F75] hover:text-white transition-colors"><Users size={11} /> Reassign</button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 w-56 rounded-lg border border-[#E5E0D8] bg-white shadow-lg p-1.5" data-testid={`reassign-menu-${r.id}`}>
          <p className="text-[10px] uppercase tracking-wide text-[#8FA89B] px-2 pt-1 pb-1.5">Qualified at this location</p>
          {providers.length === 0 ? <p className="text-xs text-[#8FA89B] px-2 py-1.5">No other certified providers here.</p>
            : providers.map((p) => (
              <button key={p.enhancer_id} disabled={busy || p.enhancer_id === r.enhancer_id} onClick={() => assign(p.enhancer_id)}
                data-testid={`reassign-to-${p.enhancer_id}-${r.id}`}
                className={`w-full text-left text-sm px-2.5 py-1.5 rounded-md hover:bg-[#144F75]/10 disabled:opacity-60 ${p.enhancer_id === r.enhancer_id ? "text-[#144F75] font-medium" : "text-[#1A242B]"}`}>
                {p.name}{p.enhancer_id === r.enhancer_id ? " · current" : ""}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

function ActionButtons({ r, actionsFor, setStatus, acting, canDelete, onDelete }) {
  const btns = actionsFor(r);
  return (
    <>
      {btns.map((a) => (
        <button key={a.s} onClick={() => setStatus(r.id, a.s)} disabled={acting === r.id + a.s}
          data-testid={`appt-action-${a.s}-${r.id}`} title={a.label}
          className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-[#F1EEE8] text-[#4A5D6B] hover:bg-[#144F75] hover:text-white transition-colors disabled:opacity-50">
          {acting === r.id + a.s ? <Loader2 size={11} className="animate-spin" /> : <a.Icon size={11} />} {a.label}
        </button>
      ))}
      {canDelete && (
        <button onClick={() => onDelete(r.id)} disabled={acting === r.id + "del"}
          data-testid={`appt-delete-${r.id}`} title="Delete (unconfirmed & unpaid only)"
          className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-[#B45151]/12 text-[#B45151] hover:bg-[#B45151] hover:text-white transition-colors disabled:opacity-50">
          {acting === r.id + "del" ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />} Delete
        </button>
      )}
      {btns.length === 0 && !canDelete && <span className="text-xs text-[#8FA89B]">—</span>}
    </>
  );
}
